<template>
  <v-card flat class="pa-4">
    <v-form ref="form">
      <v-tooltip bottom max-width="400" nudge-top="20" open-delay="1000">
        <template v-slot:activator="{on}">
          <v-switch v-model="settings.exciseAccrual"
                    @change="settings.exciseCreateReceipt = settings.exciseCreateReceipt && settings.exciseAccrual; validate()">
            <span slot="label" v-on="on">Add excise to spirits/products cost when incurred</span>
          </v-switch>
        </template>
        When selected, PŪRTRAK will add the excise payable to the cost of spirits or products when
        excise becomes payable.
        This will be done for:
        <ul>
          <li>Products moved to duty-paid from in-bond.</li>
          <li>Imported spirits removed to licensed users.</li>
          <li>Other duty-paid reductions to products or spirits.</li>
        </ul>
        Also, if connected to QuickBooks Online, the Excise Liability account will be credited.
      </v-tooltip>

      <account-picker v-if="$store.getters.company.connectedToAccounting"
                      v-model="companyAccounts.exciseLiability" label="Excise liability account"
                      :disabled="!settings.exciseAccrual"
                      :accounts="liabilityAccounts" :addTypes="['OtherCurrentLiability']"
                      @change="updateAccounts"/>

      <v-tooltip bottom max-width="300" nudge-top="20" open-delay="1000">
        <template v-slot:activator="{on}">
          <v-switch v-model="settings.exciseCreateReceipt" @change="validate()" :disabled="!settings.exciseAccrual">
            <span slot="label" v-on="on">Create inventory receipt for excise payments</span>
          </v-switch>
        </template>
        When selected, filing a CRA Excise report will create an Invenetory Receipt transaction for any payment due.
      </v-tooltip>

      <v-autocomplete v-model="settings.excisePaymentToId" outlined dense label="CRA payee" :rules="[supplier]"
                      :items="suppliers" item-text="name" item-value="id" no-data-text="No suppliers found"
                      :disabled="!settings.exciseCreateReceipt">
        <template v-slot:prepend-item>
          <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="supplierCreate=true">
            <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new supplier
          </div>
        </template>
      </v-autocomplete>

      <template v-if="$store.getters.company.connectedToAccounting">
        <v-autocomplete label="QuickBooks payment method" v-model="settings.excisePaymentMethodId"
                        :items="paymentMethods" item-value="id" item-text="name" outlined dense
                        :disabled="!settings.exciseCreateReceipt"/>

        <account-picker v-model="settings.exicsePaymentAccountRef"
                        label="Payment account"
                        :accounts="paymentAccounts"
                        :disabled="!settings.excisePaymentMethodId || !settings.exciseCreateReceipt"
                        required/>
      </template>

      <v-autocomplete label="Payment item for excise remittance" v-model="settings.excisePaymentItemId" :rules="[fieldRules.required]"
                      :items="items" item-value="id" item-text="name" outlined dense class="mt-7"
                      :disabled="!settings.exciseCreateReceipt">
        <template v-slot:prepend-item>
          <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="createDefaultExciseItem">
            <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Create default excise payment item
          </div>
        </template>
      </v-autocomplete>

    </v-form>
    <supplier-quick-create v-model="supplierCreate" @create="newSupplier" defaultName="Receiver General for Canada - Excise Duty"/>
  </v-card>
</template>

<script>
import settingsPreferences from '@/views/settings/preferences/settingsPreferences'
import SupplierQuickCreate from '@/views/purchasing/suppliers/SupplierQuickCreate'

export default {
  name: 'SettingsPreferencesGeneralExcise',
  mixins: [settingsPreferences],
  components: {SupplierQuickCreate},
  data() {
    return {
      suppliers: [],
      supplierCreate:false,
      paymentAccounts: [],
      liabilityAccounts: [],
      paymentMethods: [{id:'',name:'Do not make a QuickBooks payment'},{divider:true}],
      items: [],
      companyAccounts: {},
      supplier: v => v != 0 || 'Required'
    }
  },
  async created() {
    this.$lazyListLoad(this.suppliers, 'vendors2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.items, 'items2', 'id,name,type', 100, [{field:'active', comparator:'eq', value:'true'},{field:'type',comparator:'ne',value:'Item'}]);
    this.$lazyListLoad(this.paymentMethods, 'paymentMethods2', 'id,name', 100);

    const results = await this.$apiCall(`query SettingsPreferencesGeneralExcise {
                                              accounts {
                                                ref,name,type,typeName,currency
                                              }
                                              companyAccounts {
                                                equityBalance,exciseLiability,inventoryAdjustment,
                                                itemInventory,itemCOG,itemIncome,
                                                nonInventoryUsage,nonInventoryExpense,nonInventoryIncome,
                                                serviceUsage,serviceExpense,serviceIncome,
                                                caskInventory,
                                                spiritInventory,spiritCOG,spiritIncome,
                                                nonExciseInventory,nonExciseCOG,nonExciseIncome,
                                                wineInventory,wineCOG,wineIncome,
                                                bondedProductInventory,bondedProductCOG,bondedProductIncome,
                                                dutyPaidProductInventory,dutyPaidProductCOG,dutyPaidProductIncome,
                                                bundleIncome,
                                                fermenterInventory,tankInventory
                                              }
                                          }`,
                                        {});
    if (!results.error) {
      this.paymentAccounts = results.data.accounts.filter(account =>
        account.currency === this.$store.getters.company.settings.homeCurrency &&
        (account.type === 'Bank' || account.type === 'CreditCard' || account.type === 'OtherCurrentAsset')
      )
      this.liabilityAccounts = results.data.accounts.filter(account => account.type === 'OtherCurrentLiability');
      this.companyAccounts = results.data.companyAccounts;
    }
    this.validate();
  },
  methods: {
    validate() {
      this.$nextTick(() => this.$refs.form.validate());
    },
    async newSupplier(supplier) {
      this.suppliers.push(supplier);
      this.settings.excisePaymentToId = supplier.id;
    },
    async createDefaultExciseItem() {
      const input = {type:'Service', name:'Excise remittance',sku:'',active:true,
                     location:'',lotTracking:false,trackUnits:'',precision:0,usageCost:0,
                     allowNegativeInventory:false,reorderPoint:0,
                     inventoryAccountRef:this.companyAccounts.serviceUsage,
                     incomeAccountRef:this.companyAccounts.serviceIncome,
                     cogAccountRef:this.companyAccounts.exciseLiability,
                     buyingUnits:'',buyingPrecision:0,convertToTracking:1,
                     weight:0,upc:'',reorderQuantity:0,reorderDays:0,reorderBusinessDays:false,
                     buyingDescription:'',sold:false,sellingDescription:'',defaultPrice:0,commissionExempt:false,
                     assembled:false,bom:[],customers:[],vendors:[],initialAsof:'2021-01-01 00:00:00'};
      const results = await this.$apiCall(`mutation SettingsPreferencesGeneralExcise($input: ItemInput!) {
                                             itemCreate(input:$input) {id,name,type,trackUnits,precision,category{id},active,sold}
                                           }`, {input});
      if (!results.error) {
        this.items.push(results.data.itemCreate);
        this.$store.commit('itemAdd', results.data.itemCreate);
        this.settings.excisePaymentItemId = results.data.itemCreate.id;
      }
    },
    async updateAccounts() {
      const results = await this.$apiCall(`mutation AppsQboAccounts($input:CompanyAccountsInput!) {
                                             companySetAccounts(input:$input) {
                                                equityBalance
                                              }
                                           }`,
                                          {input:this.companyAccounts});
    }
  }
}
</script>